import { Vue, Component, Prop, Watch, Setup } from "@wagich/vue-facing-decorator-metadata";
import { nanoid } from "nanoid";
import { ref, reactive, type CSSProperties } from "vue";
import { useFloating, autoUpdate, offset, flip, shift, type Placement } from "@floating-ui/vue";

import { render } from "./popover-card.html";

interface FloatingState {
	setReference: (el: HTMLElement | null) => void;
	setFloating: (el: HTMLElement | null) => void;
	floatingStyles: CSSProperties;
	placement: Placement;
	update: () => Promise<void>;
}

@Component({
	render,
})
export class PopoverCard extends Vue {
	@Prop({ required: false }) class: string | null;
	@Prop({ required: false, default: false }) open: boolean;

	id: string = `popover-${nanoid(8)}`;
	isOpen: boolean = false;

	@Setup(() => {
		const referenceEl = ref<HTMLElement | null>(null);
		const floatingEl = ref<HTMLElement | null>(null);
		const floating = useFloating(referenceEl, floatingEl, {
			placement: "bottom",
			middleware: [offset(4), flip(), shift({ padding: 8 })],
			whileElementsMounted: autoUpdate,
		});
		return reactive({
			setReference(el: HTMLElement | null) { referenceEl.value = el; },
			setFloating(el: HTMLElement | null) { floatingEl.value = el; },
			floatingStyles: floating.floatingStyles,
			placement: floating.placement,
			update: floating.update,
		}) as FloatingState;
	})
	fl!: FloatingState;

	get floatingStyle(): CSSProperties {
		return { ...this.fl.floatingStyles, zIndex: 30 };
	}

	get iconName(): string {
		const opensUp = this.fl.placement.startsWith("top");
		if (this.isOpen) {
			return opensUp ? "arrow-down" : "arrow-up";
		}
		return opensUp ? "arrow-up" : "arrow-down";
	}

	@Watch("open")
	onOpenChanged(newValue: boolean) {
		this.isOpen = newValue;
	}

	mounted() {
		this.isOpen = this.open;
		document.addEventListener("click", this.onDocumentClick);
	}

	beforeUnmount() {
		document.removeEventListener("click", this.onDocumentClick);
	}

	toggle() {
		this.isOpen = !this.isOpen;
		if (this.isOpen) {
			void this.fl.update();
		}
	}

	close() {
		this.isOpen = false;
	}

	private onDocumentClick(e: MouseEvent) {
		if (!this.isOpen) {
			return;
		}
		const target = e.target as Node;
		if (this.$el.contains?.(target)) {
			return;
		}
		this.isOpen = false;
	}
}
